import { api } from "../configs/apiConfig"; // Ensure the path is correct

const API_URL = "/Page"; // Base URL is already set in apiConfig.js

const PageService = {
    /**
     * Fetch all pages, including their hierarchical structure.
     */
    getAll: async () => {
        try {
            const response = await api.get(API_URL);
            return response.data;
        } catch (error) {
            console.error("Error fetching pages:", error.response?.data || error.message);
            throw new Error(error.response?.data?.message || "Failed to fetch pages.");
        }
    },

    /**
     * Fetch only active pages.
     */
    getActive: async () => {
        try {
            const response = await api.get(`${API_URL}/active`);
            return response.data;
        } catch (error) {
            console.error("Error fetching active pages:", error.response?.data || error.message);
            throw new Error(error.response?.data?.message || "Failed to fetch active pages.");
        }
    },

    /**
     * Fetch a specific page by ID, including its children.
     * @param {number} id - The page ID.
     */
    getById: async (id) => {
        try {
            const response = await api.get(`${API_URL}/${id}`);
            return response.data;
        } catch (error) {
            console.error(`Error fetching page with ID ${id}:`, error.response?.data || error.message);
            throw new Error(error.response?.data?.message || `Failed to fetch page ${id}.`);
        }
    },

    /**
     * Fetch child pages for a specific parent page.
     * @param {number} parentId - The parent page ID.
     */
    getChildPages: async (parentId) => {
        try {
            const response = await api.get(`${API_URL}/children/${parentId}`);
            return response.data;
        } catch (error) {
            console.error(`Error fetching child pages for parent ID ${parentId}:`, error.response?.data || error.message);
            throw new Error(error.response?.data?.message || `Failed to fetch child pages for parent ${parentId}.`);
        }
    },

    /**
     * Add a new page, ensuring parent-child relationships.
     * @param {FormData} formData - The form data for the new page.
     */
    add: async (formData) => {
        try {
            const response = await api.post(API_URL, formData, {
                headers: {
                    'Content-Type': 'multipart/form-data'
                }
            });
            return response.data;
        } catch (error) {
            console.error("Error adding page:", error.response?.data || error.message);
            throw new Error(error.response?.data?.message || "Failed to add page.");
        }
    },

    /**
     * Update an existing page, ensuring valid parent-child relationships.
     * @param {number} id - The page ID.
     * @param {FormData} formData - The updated form data for the page.
     */
    update: async (id, formData) => {
        try {
            await api.put(`${API_URL}/${id}`, formData, {
                headers: {
                    'Content-Type': 'multipart/form-data'
                }
            });
        } catch (error) {
            console.error(`Error updating page with ID ${id}:`, error.response?.data || error.message);
            throw new Error(error.response?.data?.message || `Failed to update page ${id}.`);
        }
    },

    /**
     * Delete a page and all its child pages recursively.
     * @param {number} id - The page ID.
     */
    delete: async (id) => {
        try {
            await api.delete(`${API_URL}/${id}`);
        } catch (error) {
            console.error(`Error deleting page with ID ${id}:`, error.response?.data || error.message);
            throw new Error(error.response?.data?.message || `Failed to delete page ${id}.`);
        }
    },

    /**
     * Reorder pages under a specific parent (or top-level pages if parentId is null)
     * @param {number|null} parentId - The parent page ID, or null for top-level pages
     * @param {Array<number>} orderedIds - Array of page IDs in the desired order
     */
    reorderPages: async (parentId, orderedIds) => {
        try {
            await api.post(`${API_URL}/reorder`, {
                parentId: parentId,
                orderedIds: orderedIds
            });
        } catch (error) {
            console.error("Error reordering pages:", error.response?.data || error.message);
            throw new Error(error.response?.data?.message || "Failed to reorder pages.");
        }
    }
};

export default PageService;
